import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { BookPreviewModal } from '../components/BookPreviewModal';
import { toast } from 'sonner';
import { ShoppingCart, ArrowLeft, Loader2, Check, BookOpen, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookDetailPage = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await axios.get(`${API}/books/${bookId}`);
        setBook(response.data);
      } catch (error) {
        console.error('Error fetching book:', error);
        toast.error('Book not found');
        navigate('/browse');
      }
      setLoading(false);
    };
    fetchBook();
  }, [bookId, navigate]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    setAddingToCart(true);
    try {
      await addToCart(book.book_id);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error(error.message || 'Failed to add to cart');
    }
    setAddingToCart(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!book) return null;

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {/* Book Cover */}
          <div className="relative">
            <div className="aspect-[2/3] overflow-hidden rounded-sm bg-muted sticky top-24">
              <img
                src={book.cover_image}
                alt={book.title}
                className="w-full h-full object-cover img-matte"
                data-testid="book-cover"
              />
              {book.is_new_release && (
                <Badge className="absolute top-4 left-4 font-accent text-[10px] uppercase tracking-wider bg-primary">
                  New Release
                </Badge>
              )}
            </div>
          </div>

          {/* Book Details */}
          <div>
            <div className="mb-6">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-primary mb-2">
                {book.category}
              </p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-3" data-testid="book-title">
                {book.title}
              </h1>
              <p className="text-xl text-muted-foreground">by {book.author}</p>
            </div>

            <div className="flex items-baseline gap-4 mb-8">
              <span className="font-heading text-4xl font-bold text-primary" data-testid="book-price">
                ${book.price.toFixed(2)}
              </span>
              {book.stock > 0 ? (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <Check className="h-4 w-4" />
                  In Stock
                </span>
              ) : (
                <span className="text-sm text-destructive">Out of Stock</span>
              )}
            </div>

            <p className="text-muted-foreground leading-relaxed mb-8" data-testid="book-description">
              {book.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Button
                size="lg"
                className="flex-1 btn-lift"
                onClick={handleAddToCart}
                disabled={addingToCart || book.stock === 0}
                data-testid="add-to-cart-button"
              >
                {addingToCart ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ShoppingCart className="h-4 w-4 mr-2" />
                )}
                Add to Cart
              </Button>
              
              {book.preview_content && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                  data-testid="preview-button"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Read Preview
                </Button>
              )}
            </div>

            <Separator className="my-8" />

            {/* Book Details */}
            <div className="space-y-4">
              <h3 className="font-heading text-xl font-semibold">Book Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {book.isbn && (
                  <div>
                    <p className="text-muted-foreground">ISBN</p>
                    <p className="font-medium">{book.isbn}</p>
                  </div>
                )}
                {book.pages && (
                  <div>
                    <p className="text-muted-foreground">Pages</p>
                    <p className="font-medium">{book.pages}</p>
                  </div>
                )}
                {book.published_date && (
                  <div>
                    <p className="text-muted-foreground">Published</p>
                    <p className="font-medium">{book.published_date}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Category</p>
                  <p className="font-medium">{book.category}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <BookPreviewModal
        bookId={book.book_id}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
};
